import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  InputAdornment,
  Alert as MuiAlert,
  TextField,
  Typography,
} from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AdminUserContext } from "contexts/Admin";
import { ChangeEvent, useContext, useState } from "react";

import Alert from "components/Alert";
import Editor from "components/Editor";
import { extensions } from "components/Editor/utils";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import styles from "../styles";
import PreviewDialog from "./PreviewDialog";
import { EmailDraft } from "./schema";

const EmailCampaign = () => {
  const { getToken } = useAuth();
  const context = useContext(AdminUserContext);
  const { currentAdminUser } = {
    ...context,
  };
  const [file, setFile] = useState<File | null>(null);
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    setFile(selectedFile || null);
  };
  const [emailDraft, setEmailDraft] = useState<EmailDraft>({
    subject: "",
    body: "",
  });
  const [preview, setPreview] = useState<EmailDraft>({
    subject: "",
    body: "",
  });
  const [inSeconds, setInSeconds] = useState<number>(300);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [message, severity, setAlert, closeAlert] = useAlert();
  const [sendFrom, setSendFrom] = useState<string>("hello@onbento.com");

  const [failedEmails, setFailedEmails] = useState<string[]>([]);
  const [successCount, setSuccessCount] = useState<number>(0);

  const editor = useEditor(
    {
      extensions: extensions({
        placeholderText:
          "Hi {First Name}, Bento's ever-growing database now has 26,000 contacts that have worked with users to land then brand collabs. I wanted to quickly let you know that creators who send at least 20 emails on Bento are 10x more likely to land a brand partnership!!",
      }),
      content: emailDraft?.body,
      onUpdate({ editor }) {
        setEmailDraft((prev) => {
          return { ...prev, body: editor.getHTML() };
        });
      },
    },
    [],
  ) as TipTapEditor;

  const subjectEditor = useEditor(
    {
      extensions: extensions({
        isPlain: true,
        placeholderText: "Bento found {Z} more opportunities for you",
      }),
      content: emailDraft?.subject,
      onUpdate({ editor }) {
        setEmailDraft((prev) => {
          return { ...prev, subject: editor.getHTML() };
        });
      },
    },
    [],
  ) as TipTapEditor;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!file) {
        throw new Error("You must select a CSV file to upload");
      }
      const data = _generateFormData();
      const res = await fetcherAuth(
        getToken,
        `/api/admin/email-campaigns/send`,
        "POST",
        {},
        data,
        false,
        true,
      );
      setSuccessCount(res.successCount);
      setFailedEmails(res.failedEmails);
      setAlert(
        `Successfully sent email campaign to ${res.successCount} people`,
        "success",
      );
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const data = _generateFormData();
      if (!data) return;
      setTestLoading(true);
      await fetcherAuth(
        getToken,
        `/api/admin/email-campaigns/test`,
        "POST",
        {},
        data,
        false,
        true,
      );
      setAlert(`Sent test email to ${currentAdminUser?.email}`, "success");
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setTestLoading(false);
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const data = _generateFormData();
      if (!data) return;
      const res = await fetcherAuth(
        getToken,
        `/api/admin/email-campaigns/preview`,
        "POST",
        {},
        data,
        false,
        true,
      );
      setOpenPreviewDialog(true);
      setPreview({ subject: res.subject, body: res.body });
    } catch (error) {
      setAlert(error?.message, "warning");
    }
  };

  const _generateFormData = () => {
    if (!file) {
      throw new Error("You must select a CSV file to upload");
    }
    const data = new FormData();
    const fileExt = file.name.split(".").pop()?.toLowerCase();
    if (fileExt !== "csv") {
      throw new Error("You must select a CSV file");
    }
    data.append("file", file);
    data.append("subject", JSON.stringify(emailDraft.subject));
    data.append("body", JSON.stringify(emailDraft.body));
    data.append("sendFrom", sendFrom);
    return data;
  };

  const missing = !emailDraft.subject || !emailDraft.body || !file;

  return (
    <Box sx={{ pt: 3, pb: 3 }}>
      <Typography variant="h4">Email Campaign</Typography>

      {(successCount > 0 || failedEmails?.length > 0) && (
        <Box sx={{ my: 3 }}>
          <Typography sx={{ fontSize: 14 }}>
            <strong>Campaign Results</strong>
          </Typography>

          {successCount > 0 && (
            <MuiAlert
              sx={{ my: 1 }}
              icon={<Box component="i" className="fa-regular fa-check" />}
              severity="success"
            >
              Total Success: {successCount}
            </MuiAlert>
          )}

          {failedEmails.length > 0 && (
            <MuiAlert
              sx={{ my: 1 }}
              icon={<Box component="i" className="fa-regular fa-xmark" />}
              severity="error"
            >
              Cannot send email to these recipients:
              <ul>
                {failedEmails.map((email, index) => (
                  <li key={index}>{email}</li>
                ))}
              </ul>
            </MuiAlert>
          )}

          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      <FormHelperText>
        By default, email will be schedule within 5 minutes. To send instantly,
        set the number of seconds to 0.
      </FormHelperText>

      <Grid container alignItems="center" sx={{ mt: 1, mb: 3 }} gap={2}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || missing}
          disableElevation
        >
          Send{" "}
          <Box
            component="i"
            className="fa-regular fa-paper-plane"
            sx={{ ml: 1 }}
          />
        </Button>

        <Button
          variant="outlined"
          disabled={loading || missing}
          onClick={handlePreviewEmail}
        >
          Preview Email{" "}
          <Box component="i" className="fa-regular fa-eye" sx={{ ml: 1 }} />
        </Button>

        <TextField
          sx={{ width: 150 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">seconds</InputAdornment>
            ),
          }}
          size="small"
          type="number"
          placeholder="300"
          onChange={(e) => setInSeconds(Number(e.target.value))}
          value={inSeconds}
        />
      </Grid>

      <Grid container item xs={12} gap={3}>
        <Grid item xs md>
          <Box>
            <FormHelperText>Send From: </FormHelperText>
            <TextField
              placeholder="hello@onbento.com"
              value={sendFrom}
              onChange={(e) => setSendFrom(e.target.value)}
            />
          </Box>
          <Box>
            <FormHelperText>Subject: </FormHelperText>
            <Editor editor={subjectEditor} hideMenu />
          </Box>

          <Box>
            <FormHelperText>Body</FormHelperText>
            <Editor editor={editor} allowImage={true} showVariableError />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography gutterBottom sx={{ mt: 3 }} variant="h6">
            CSV of Recipients
          </Typography>
          <Typography sx={{ maxWidth: 600 }}>
            <strong>Important rules to note when uploading: </strong>

            <li>
              If you use a variable in the email, make sure the variable match
              exactly the column's name. (e.g{" "}
              <em>
                <strong>
                  There are &#123;X&#125; contacts to reach out...
                </strong>
              </em>{" "}
              then the CSV must have a column named{" "}
              <em>
                <strong>X</strong>
              </em>
              )
            </li>
            <li>
              You must have a column named `email` and a column named `name`.
              SendGrid will use these columns to send the email to the right
              person.
            </li>
          </Typography>
          <FormControl sx={styles.file}>
            <Input type="file" onChange={handleFileChange} />
          </FormControl>
        </Grid>
      </Grid>
      <Alert message={message} severity={severity} closeAlert={closeAlert} />

      {openPreviewDialog && (
        <PreviewDialog
          open={openPreviewDialog}
          close={() => setOpenPreviewDialog(false)}
          preview={preview}
          handleSubmit={handleSendTestEmail}
          testLoading={testLoading}
        />
      )}
    </Box>
  );
};

export default EmailCampaign;
